/**
 * Locale-aware formatting for WagePro.
 *
 * Every function is pure and takes the locale explicitly — nothing reads a
 * global, so all of it is unit-testable without React. Money always arrives as
 * integer minor units (docs/money-rules.md) and rates as basis points; this
 * module is the only place either is turned into a display string.
 */

import type {
  BasisPoints,
  CurrencyCode,
  IsoDate,
  IsoDateTime,
  MinorUnits,
  PayPeriod,
} from '../data/types';

/** Minor units per major unit. LKR, like most currencies, has two decimals. */
const MINOR_UNITS_PER_MAJOR = 100;

/** Decimal places the minor-unit scale carries -- `2` for a scale of 100. */
const MINOR_UNIT_DECIMALS = Math.round(Math.log10(MINOR_UNITS_PER_MAJOR));

/** Basis points in 100% — `10_000 bp === 1.0`. */
const BASIS_POINTS_PER_UNIT = 10_000;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3_600;
const SECONDS_PER_DAY = 86_400;

/** Options shared by `format_money` and `format_money_deduction`. */
export interface FormatMoneyOptions {
  /**
   * Always print the minor-unit decimals (`LKR 285,000.00`), even for a whole
   * amount. Use it wherever several amounts are read together as one
   * statement -- a payslip -- so a whole gross never sits next to a
   * fractional net pay in a different format (WagePro bug-bash 2026-10-15,
   * #10).
   *
   * Assumes scale 100 (`MINOR_UNITS_PER_MAJOR`) -- payslip currency is
   * LKR-only (`CurrencyCode`); revisit when `CurrencyCode` widens (IDR sen vs
   * whole-rupiah, 0/3-decimal currencies).
   */
  readonly fixed_decimals?: boolean;
}

/**
 * Format an integer minor-unit amount.
 *
 * By default whole amounts print without decimals (payroll figures are
 * quoted in whole rupees on the employer screens); anything with a fractional
 * part keeps both decimal places so a cent is never silently rounded away on
 * screen. Pass `{ fixed_decimals: true }` for statement-style figures that
 * must all share one format (see `FormatMoneyOptions`).
 *
 * `currency` accepts any ISO 4217 string (not narrowed to `CurrencyCode`) --
 * this is a thin `Intl.NumberFormat` wrapper, so a real, non-`'LKR'` code
 * (a genuine foreign-currency expense claim pre-approval, #22792) formats
 * correctly with no change needed here beyond the parameter's own type.
 */
export function format_money(
  amount: MinorUnits,
  currency: string,
  locale: string,
  options: FormatMoneyOptions = {},
): string {
  const is_whole = amount % MINOR_UNITS_PER_MAJOR === 0;
  const fraction_digits =
    options.fixed_decimals === true || !is_whole ? MINOR_UNIT_DECIMALS : 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fraction_digits,
    maximumFractionDigits: fraction_digits,
  }).format(amount / MINOR_UNITS_PER_MAJOR);
}

/**
 * Format a minor-unit amount with an explicit leading minus (deductions).
 *
 * The sign is joined with a no-break space (U+00A0), never a plain space, so
 * a narrow column can't strand the "−" on its own line (WagePro bug-bash
 * 2026-10-15, #2). ICU already joins the currency code to the digits with a
 * no-break space.
 */
export function format_money_deduction(
  amount: MinorUnits,
  currency: CurrencyCode,
  locale: string,
  options: FormatMoneyOptions = {},
): string {
  return `−\u00A0${format_money(Math.abs(amount), currency, locale, options)}`;
}

/** Compact money for KPI tiles — `515_000_000` → `LKR 5.15M`. */
export function format_money_compact(
  amount: MinorUnits,
  currency: CurrencyCode,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(amount / MINOR_UNITS_PER_MAJOR);
}

/** `800` → `8%`, `1_850` → `18.5%`. */
export function format_basis_points(rate: BasisPoints, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(rate / BASIS_POINTS_PER_UNIT);
}

/** `15_000` basis points → `1.5` — the multiplier for an overtime rate. */
export function format_multiplier(
  multiple_bp: BasisPoints,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
  }).format(multiple_bp / BASIS_POINTS_PER_UNIT);
}

/**
 * Leave day count (already unscaled) — whole numbers without decimals,
 * fractional values to two. Shared by the admin Leave → Balances tab and the
 * employee's My Leave balance card so both always print the same figure.
 */
export function format_leave_days(days: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: days % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(days);
}

export function format_number(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** Signed count for a delta line — `2` → `+2`. */
export function format_signed_number(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { signDisplay: 'exceptZero' }).format(
    value,
  );
}

/** Signed percentage from basis points — `260` → `+2.6%`. */
export function format_signed_basis_points(
  rate: BasisPoints,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  }).format(rate / BASIS_POINTS_PER_UNIT);
}

/**
 * Parse either an ISO-8601 calendar date (`2026-04-30`) or a full ISO-8601
 * instant (`2026-04-30T09:12:00.000Z`) into a `Date`.
 *
 * Both forms reach these formatters because `IsoDate` and `IsoDateTime` are
 * both bare `string` aliases (see `data/types.ts`), so TypeScript cannot tell
 * a caller passing one from a caller passing the other. Blindly appending
 * `T00:00:00Z` to an instant yields `2026-04-30T09:12:00.000ZT00:00:00Z` — an
 * Invalid Date, which makes `Intl.DateTimeFormat.formatToParts` throw
 * `RangeError: Invalid time value`. That is not a recoverable display glitch:
 * thrown from render with no error boundary above it, it unmounts the whole
 * tree and the user gets a blank white page (hit on the employee
 * "Self-service access" tab, whose `employee_ess_invites.created_at` /
 * `expires_at` are `new Date().toISOString()` timestamps, not calendar dates).
 *
 * Every caller formats with `timeZone: 'UTC'`, so an instant renders its UTC
 * calendar date and a date-only string is completely unaffected.
 *
 * The parameter is a bare `string` rather than `IsoDate | IsoDateTime` for the
 * very reason this helper exists: both aliases resolve to `string`, so that
 * union is a no-op that `@typescript-eslint/no-duplicate-type-constituents`
 * rejects outright.
 */
function to_utc_date(at: string): Date {
  return new Date(at.includes('T') ? at : `${at}T00:00:00Z`);
}

/**
 * Dates are calendar dates, not instants — they are formatted in UTC so a
 * viewer west of Greenwich never sees `12 Jun` render as `11 Jun`.
 *
 * Assembled from parts rather than taken from `format()` so the field order
 * stays whatever CLDR says for the locale while the punctuation between the
 * fields does not. Tamil's pattern is `01 ஜன., 2026` — an abbreviation stop
 * inside the month name plus a comma separator — which reads as clutter in a
 * dense payroll table. Dropping both gives `01 ஜன 2026`, and it is a no-op
 * for en-001 (`01 Jan 2026`) and si-LK (`2026 දුරුතු 01`), whose patterns
 * already separate every field with a single space.
 */
export function format_date(date: IsoDate, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .formatToParts(to_utc_date(date))
    .filter((part) => part.type !== 'literal')
    .map((part) =>
      part.type === 'month' ? part.value.replace(/\.$/, '') : part.value,
    )
    .join(' ');
}

/** A range separator the locale puts between the two dates (`–`, `-`, `~`). */
const RANGE_SEPARATOR = /^\s*[-\u2010-\u2015~\u301C]\s*$/;

/**
 * An inclusive calendar-date range, collapsing whatever the two ends share —
 * `22 Oct 2026` … `23 Oct 2026` → `22–23 Oct 2026`, across months
 * `30 Oct – 2 Nov 2026`, across years the two full dates — and a single day
 * as just that date (WagePro bug-bash 2026-10-15, #11).
 *
 * Which fields collapse, and the separator, are CLDR's per locale
 * (`Intl.DateTimeFormat#formatRangeToParts`), not hand-rolled. Formatted in
 * UTC for the same reason as `format_date`, and with the same punctuation
 * clean-up: every literal except the range separator itself becomes a single
 * space and a month abbreviation drops its trailing stop.
 *
 * The day is `numeric`, not `format_date`'s `2-digit`: ICU's interval
 * patterns ignore 2-digit padding (a range prints `1–2 Jan`, never
 * `01–02 Jan`), so every case — range, single day and reversed pair — goes
 * through this one formatter to keep a list of ranges consistent.
 *
 * A reversed range (end before start) is never collapsed — it is printed
 * end-to-end as given rather than handed to `formatRangeToParts`, whose
 * behaviour on a reversed pair has differed between engines. The same
 * end-to-end form is the fallback where `formatRangeToParts` does not exist
 * at all (Android System WebView before 76): calling it there would throw
 * from render and take the whole My leave screen down.
 */
export function format_date_range(
  start: IsoDate,
  end: IsoDate,
  locale: string,
): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const one = (date: IsoDate): string =>
    join_date_parts(formatter.formatToParts(to_utc_date(date)));
  if (start === end) return one(start);
  // Typed as always present; an old engine may still lack it.
  const ranged: Partial<Pick<Intl.DateTimeFormat, 'formatRangeToParts'>> =
    formatter;
  if (end < start || ranged.formatRangeToParts === undefined) {
    return `${one(start)} \u2013 ${one(end)}`;
  }
  return join_date_parts(
    formatter.formatRangeToParts(to_utc_date(start), to_utc_date(end)),
  );
}

/** `format_date_range`'s clean-up of a part list (see there). */
function join_date_parts(parts: readonly Intl.DateTimeFormatPart[]): string {
  return parts
    .map((part) => {
      if (part.type === 'month') return part.value.replace(/\.$/, '');
      if (part.type !== 'literal') return part.value;
      return RANGE_SEPARATOR.test(part.value) ? part.value : ' ';
    })
    .join('')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** Month + year only — `2026-04-30` → `April 2026`. */
export function format_month_year(period: PayPeriod, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(period.year, period.month - 1, 1)));
}

/**
 * A standalone month name for a picker — `4` → `April` / `ஏப்ரல்`.
 *
 * `month` is 1-based, matching how the fiscal-year setting is stored. The day
 * is pinned to the 1st of an arbitrary non-leap year: only the month field is
 * read, so the year and day never surface.
 */
export function format_month_name(month: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2001, month - 1, 1)));
}

/** Day-of-month only, for a range's opening bound. */
export function format_day(date: IsoDate, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    timeZone: 'UTC',
  }).format(to_utc_date(date));
}

export function format_time(at: IsoDateTime, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(new Date(at));
}

/**
 * A coarse "how long ago" label. Deliberately capped at days — anything older
 * than a week reads better as an absolute date, which callers render instead.
 */
export function format_relative_time(
  at: IsoDateTime,
  now: IsoDateTime,
  locale: string,
): string {
  const elapsed_seconds = Math.round(
    (new Date(at).getTime() - new Date(now).getTime()) / 1_000,
  );
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const magnitude = Math.abs(elapsed_seconds);

  if (magnitude < SECONDS_PER_HOUR) {
    return formatter.format(
      Math.round(elapsed_seconds / SECONDS_PER_MINUTE),
      'minute',
    );
  }
  if (magnitude < SECONDS_PER_DAY) {
    return formatter.format(
      Math.round(elapsed_seconds / SECONDS_PER_HOUR),
      'hour',
    );
  }
  return formatter.format(Math.round(elapsed_seconds / SECONDS_PER_DAY), 'day');
}

/**
 * Whether an activity entry is recent enough for a clock time (`09:40`)
 * rather than a relative label.
 */
export function is_same_utc_day(a: IsoDateTime, b: IsoDateTime): boolean {
  return a.slice(0, 10) === b.slice(0, 10);
}

/** Initials fallback when a record has no pre-computed initials. */
export function initials_from_name(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
